// The world, drawn.
//
// The camera follows the body and is clamped to the bounds of the place it is
// standing in, so looking out of the field into the room underneath it is not
// something the renderer can be asked to do.

import { BODY_HALF, ENEMY_MAX_HP, SWORD_HALF, SWORD_REACH, SWORD_SWING, TILE } from "../../sim/constants";
import { VIEW_H, VIEW_W } from "./view";
import { MATERIAL_SIZE } from "../../content/art-jobs";
import { PALETTE } from "../../content/palette";
import { zoneAt, zoneBounds, type WorldMap } from "../../sim/map";
import { DIR_VECTORS, type Dir } from "../../sim/types";
import type { PlayerView, Snapshot } from "../../shared/protocol";
import { bodyBob, drawBody, sprite } from "./sprites";
import { drawTile, groundPattern } from "./tiles";

export interface Camera {
  x: number;
  y: number;
}

export interface LocalMotion {
  mx: number;
  my: number;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : Math.min(v, hi));

/** Top-left of the view, in world pixels. */
export function cameraFor(x: number, y: number): Camera {
  const bounds = zoneBounds(zoneAt(x, y));
  // A place narrower than the view is centred in it rather than pinned to one
  // edge — the shop room is nine tiles tall and the view is fifteen.
  const cx = bounds.w <= VIEW_W ? bounds.x + (bounds.w - VIEW_W) / 2 : clamp(x - VIEW_W / 2, bounds.x, bounds.x + bounds.w - VIEW_W);
  const cy = bounds.h <= VIEW_H ? bounds.y + (bounds.h - VIEW_H) / 2 : clamp(y - VIEW_H / 2, bounds.y, bounds.y + bounds.h - VIEW_H);
  return { x: Math.round(cx), y: Math.round(cy) };
}

function drawGround(ctx: CanvasRenderingContext2D, camera: Camera, zone: number): void {
  const id = zone === 0 ? "terrain_grass" : "terrain_floor";
  const found = sprite(id);
  ctx.fillStyle = zone === 0 ? PALETTE.grassFallback : PALETTE.floorFallback;
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  if (!found) return;
  const pattern = groundPattern(ctx, id, found.baked, MATERIAL_SIZE);
  if (!pattern) return;
  ctx.save();
  ctx.translate(-camera.x, -camera.y);
  ctx.fillStyle = pattern;
  ctx.fillRect(camera.x, camera.y, VIEW_W, VIEW_H);
  ctx.restore();
}

function drawTerrain(ctx: CanvasRenderingContext2D, map: WorldMap, camera: Camera): void {
  const tx0 = Math.floor(camera.x / TILE);
  const ty0 = Math.floor(camera.y / TILE);
  const tx1 = Math.floor((camera.x + VIEW_W - 1) / TILE);
  const ty1 = Math.floor((camera.y + VIEW_H - 1) / TILE);
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      drawTile(ctx, map, tx, ty, tx * TILE - camera.x, ty * TILE - camera.y);
    }
  }
}

/** A flat oval under a body. It is the only thing standing in for depth, and
 *  it is what stops a sprite looking pasted onto the grass. */
function drawShadow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = PALETTE.void;
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.42, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A name, centred over a body. Everyone in the world has one of three: the
 *  joke only works if they are always on. */
function drawNameplate(ctx: CanvasRenderingContext2D, x: number, y: number, name: string, sub?: string): void {
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "7px system-ui, sans-serif";
  ctx.fillStyle = PALETTE.shadow;
  ctx.fillText(name, x, y + 1);
  ctx.fillStyle = PALETTE.text;
  ctx.fillText(name, x, y);
  if (!sub) return;
  ctx.font = "6px ui-monospace, monospace";
  ctx.fillStyle = PALETTE.shadow;
  ctx.fillText(sub, x, y + 8);
  ctx.fillStyle = PALETTE.dim;
  ctx.fillText(sub, x, y + 7);
}

/** A small bar over a hurt body. It is drawn only once something has taken a
 *  hit, so a peaceful field is not covered in furniture. */
function drawHealthPips(ctx: CanvasRenderingContext2D, x: number, y: number, hp: number, maxHp: number): void {
  if (hp >= maxHp) return;
  const w = 20;
  ctx.fillStyle = PALETTE.hpBack;
  ctx.fillRect(Math.round(x - w / 2), Math.round(y), w, 3);
  ctx.fillStyle = PALETTE.hp;
  ctx.fillRect(Math.round(x - w / 2), Math.round(y), Math.round((w * Math.max(0, hp)) / maxHp), 3);
}

/** The swing: a white wedge sweeping through the box the blow actually lands
 *  in, so what you see and what the server hits are the same rectangle. */
function drawSwing(ctx: CanvasRenderingContext2D, x: number, y: number, dir: Dir, progress: number): void {
  const [dx, dy] = DIR_VECTORS[dir];
  const cx = x + dx * SWORD_REACH;
  const cy = y + dy * SWORD_REACH;
  const angle = Math.atan2(dy, dx);
  const sweep = (Math.PI * 0.9) / 2;
  ctx.save();
  ctx.globalAlpha = 0.25 + 0.55 * progress;
  ctx.strokeStyle = PALETTE.slash;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(x, y, SWORD_REACH + SWORD_HALF * 0.4, angle - sweep, angle + sweep);
  ctx.stroke();
  ctx.restore();
  ctx.save();
  ctx.globalAlpha = 0.18 * progress;
  ctx.fillStyle = PALETTE.slash;
  ctx.fillRect(cx - SWORD_HALF, cy - SWORD_HALF, SWORD_HALF * 2, SWORD_HALF * 2);
  ctx.restore();
}

const facingOf = (dir: number): "left" | "right" => (dir === 2 ? "left" : "right");

interface DrawableBody {
  y: number;
  draw(): void;
}

/** AOI hysteresis deliberately streams a margin outside the camera. Do not
 *  turn those useful network entries into canvas and text work until some part
 *  of their sprite or nameplate can actually be seen. */
const bodyOnScreen = (x: number, y: number): boolean => x >= -24 && x <= VIEW_W + 24 && y >= -48 && y <= VIEW_H + 24;

export function drawScene(
  ctx: CanvasRenderingContext2D,
  map: WorldMap,
  snap: Snapshot,
  self: PlayerView,
  camera: Camera,
  time: number,
  localMotion?: LocalMotion,
): void {
  const zone = zoneAt(self.x, self.y);

  // Clip to the place you are standing in.
  //
  // The field and the room are stamped into one grid, so a view wider than the
  // room does not show empty space past its wall — it shows the *field*,
  // hanging in the air above a shop you are standing inside. The camera cannot
  // fix that on its own for a place smaller than the view, so the frame does:
  // outside the zone there is nothing, and nothing is drawn as nothing.
  //
  // Only the floor is clipped, not the bodies standing on it. A name floats
  // seven pixels above a head, and a body against the room's top wall would
  // otherwise have its own nameplate sliced off by the frame.
  const bounds = zoneBounds(zone);
  ctx.fillStyle = PALETTE.ink;
  ctx.fillRect(0, 0, VIEW_W, VIEW_H);
  ctx.save();
  ctx.beginPath();
  ctx.rect(bounds.x - camera.x, bounds.y - camera.y, bounds.w, bounds.h);
  ctx.clip();
  drawGround(ctx, camera, zone);
  drawTerrain(ctx, map, camera);
  ctx.restore();

  // The keeper is not an entity in the simulation — he never moves, never
  // fights and has nothing to synchronise — so he is drawn straight off the
  // map. What he sells is a server rule; where he stands is scenery.
  const bodies: DrawableBody[] = [];
  if (zone === 1) {
    const kx = map.keeper.x - camera.x;
    const ky = map.keeper.y - camera.y;
    bodies.push({
      y: map.keeper.y,
      draw() {
        drawShadow(ctx, kx, ky + BODY_HALF, 9);
        drawBody(ctx, "shopkeeper", kx, ky + BODY_HALF, "right", bodyBob(time * 0.9, false));
        drawNameplate(ctx, kx, ky - 26, "shopkeeper");
      },
    });
  }

  for (const enemy of snap.enemies) {
    const ex = enemy.x - camera.x;
    const ey = enemy.y - camera.y;
    if (!bodyOnScreen(ex, ey)) continue;
    bodies.push({
      y: enemy.y,
      draw() {
        drawShadow(ctx, ex, ey + BODY_HALF - 1, 8);
        ctx.save();
        // A hurt ooze is drawn hot rather than tinted: a canvas cannot tint a
        // sprite without a second offscreen pass, and at this size a flash
        // reads the same and costs nothing.
        if (enemy.hurt) ctx.filter = "brightness(1.9)";
        drawBody(ctx, "enemy", ex, ey, "right", bodyBob(time * 1.6 + enemy.id, true));
        ctx.restore();
        drawHealthPips(ctx, ex, ey - 16, enemy.hp, ENEMY_MAX_HP);
        drawNameplate(ctx, ex, ey - 18, "enemy");
      },
    });
  }

  for (const player of snap.players) {
    if (player.dead) continue;
    const px = player.x - camera.x;
    const py = player.y - camera.y;
    if (!bodyOnScreen(px, py)) continue;
    const isSelf = player.id === self.id;
    const moving = isSelf && localMotion ? localMotion.mx !== 0 || localMotion.my !== 0 : player.moving;
    const dir = isSelf && localMotion && localMotion.mx !== 0 ? (localMotion.mx < 0 ? 2 : 3) : player.dir;
    bodies.push({
      y: player.y,
      draw() {
        drawShadow(ctx, px, py + BODY_HALF, 7);
        drawBody(ctx, "player", px, py + BODY_HALF, facingOf(dir), bodyBob(time * 1.8 + player.id, moving));
        if (player.swing > 0) drawSwing(ctx, px, py, player.dir as Dir, player.swing / SWORD_SWING);
        // The stack above a head is name, slug, then health, and it clears the
        // top of the sprite: a body is thirty pixels tall and drawn from its
        // feet, so anything below y - 26 is drawn on its face.
        drawHealthPips(ctx, px, py - 30, player.hp, snap.self.maxHp);
        // Everybody is "player". The slug under it is the only thing that
        // tells one body from another, which is exactly the joke.
        drawNameplate(ctx, px, py - 40, "player", player.slug);
      },
    });
  }

  // Painter's algorithm on the world Y, so a body further down the screen is
  // in front of one further up. It is the only depth cue a top-down game has.
  bodies.sort((a, b) => a.y - b.y);
  for (const body of bodies) body.draw();
}
